import { useCallback, useEffect, useRef, useState } from "react";

import loadLockers from "../services/loadLockers";
import type { TMapLocker } from "../types";
import { getLockerIcon, getSelectedLockerIcon } from "../utils/getMapIcon";
import setupClusterLayer from "../utils/setupClusterLayer";

type TLockersLayerParams = {
  countryCode?: string;
  lockersMap?: H.Map;
  mapUI?: H.ui.UI;
  hasLockerPopups?: boolean;
  selectedLockerId?: string;
  onLockerSelect: (locker: TMapLocker) => void;
};

const SELECTED_LOCKER_ZOOM_LEVEL = 17;

const useLockersLayer = ({
  countryCode,
  lockersMap,
  mapUI,
  hasLockerPopups,
  selectedLockerId,
  onLockerSelect,
}: TLockersLayerParams): void => {
  const [lockers, setLockers] = useState<TMapLocker[]>([]);
  const lockersLayerRef = useRef<H.map.layer.Layer>();
  const lockerMarkersRef = useRef<H.map.Marker[]>([]);
  const selectedLockerIdRef = useRef<string | undefined>(selectedLockerId);

  const loadMapLockers = useCallback(async () => {
    if (!countryCode) {
      return;
    }
    const mapLockers = await loadLockers(countryCode);
    setLockers(mapLockers);
  }, [countryCode]);

  useEffect(() => {
    loadMapLockers();
  }, [loadMapLockers]);

  const handleMarkerClick = useCallback(
    (evt: any) => {
      const target = evt.target as H.map.Marker;
      const data = target.getData();
      const position = target.getGeometry() as H.geo.IPoint;

      if (data.isCluster()) {
        const zoom = (lockersMap?.getZoom() || 0) + 1;
        lockersMap?.getViewModel()?.setLookAtData({ position, zoom }, true);
        return;
      }

      const lockerData = data.getData();
      onLockerSelect(lockerData);

      if (!hasLockerPopups) {
        return;
      }

      window.setTimeout(() => {
        if (!mapUI) {
          return;
        }

        const bubble = new H.ui.InfoBubble(position, {
          content: lockerData.name,
        });
        mapUI.addBubble(bubble);
      }, 0);
    },
    [hasLockerPopups, lockersMap, mapUI, onLockerSelect]
  );

  const initLockersLayer = useCallback(() => {
    if (!lockersMap) {
      return;
    }
    if (lockersLayerRef.current) {
      lockersMap.removeLayer(lockersLayerRef.current);
    }
    if (!lockers?.length) {
      return;
    }

    const selectedLocker = selectedLockerIdRef.current
      ? lockers.find(({ id }) => id === selectedLockerIdRef.current)
      : undefined;

    if (selectedLocker) {
      const pointToZoomTo = new H.geo.Point(selectedLocker.lat, selectedLocker.lng);
      lockersMap.getViewModel().setLookAtData({
        position: pointToZoomTo,
        zoom: SELECTED_LOCKER_ZOOM_LEVEL,
      });
    } else {
      const points = lockers.map((item) => new H.geo.Point(item.lat, item.lng));
      const bounds = H.geo.Rect.coverPoints(points);
      if (bounds) {
        lockersMap.getViewModel().setLookAtData({
          bounds,
        });
      }
    }

    const { layer: clusteringLayer, lockerMarkers } = setupClusterLayer({
      lockers,
      selectedLockerId: selectedLockerIdRef.current,
      onMarkerClick: handleMarkerClick,
    });
    lockerMarkersRef.current = lockerMarkers;

    lockersMap.addLayer(clusteringLayer);

    lockersLayerRef.current = clusteringLayer;
  }, [handleMarkerClick, lockers, lockersMap]);

  useEffect(() => {
    initLockersLayer();
  }, [initLockersLayer]);

  // Updating selected lockers marker, zooming to selected locker
  useEffect(() => {
    if (!lockersMap) {
      return;
    }

    lockerMarkersRef.current.forEach((marker) => {
      const data: H.clustering.INoisePoint = marker.getData();
      const lockerData = data.getData();

      // Previously selected locker
      if (selectedLockerIdRef.current === lockerData.id) {
        marker.setIcon(getLockerIcon());
      }

      // Currently selected locker
      if (selectedLockerId === lockerData.id) {
        marker.setIcon(getSelectedLockerIcon());
        const position = marker.getGeometry() as H.geo.IPoint;
        lockersMap.getViewModel().setLookAtData({ position, zoom: SELECTED_LOCKER_ZOOM_LEVEL }, true);
      }
    });

    selectedLockerIdRef.current = selectedLockerId;
  }, [lockersMap, selectedLockerId]);
};
export default useLockersLayer;
